<template>
  <div style="width: 100%;" v-cloak v-on:drop.prevent="droppedFiles" v-on:dragover.prevent>
    <p class="font-weight-regular mt-1">
      <v-icon color="primary">mdi-information-outline</v-icon>
      {{ $t('routes.uploadlink.maxfilesize', { size: maxfilesize }) }}
    </p>

    <v-form ref="userDataForm" @submit.prevent>
      <div class="my-4">
        <v-text-field
          v-model="uploaderName"
          :rules="uploaderNameRules"
          :label="$t('common.yourName')"
          variant="solo-filled"
          flat
          block
          hide-details="auto"
          @keydown.enter="startUploading"
        >
        </v-text-field>
      </div>
    </v-form>

    <div v-if="duplicateWarning" class="d-flex align-center my-2 px-3 warning-message" role="alert" aria-live="polite">
      <v-icon class="mr-2" aria-hidden="true">mdi-alert</v-icon>
      <span class="text-body-1">{{ duplicateWarning }}</span>
      <v-spacer />
      <v-btn
        @click="duplicateWarning = null"
        icon="mdi-close-circle"
        size="x-small"
        variant="text"
        density="compact"
        :aria-label="$t('menus.uploadFiles.closeWarning')"
      />
    </div>

    <!-- Browse Files & Folders Section -->
    <div class="file-browser-section mb-4">
      <v-row>
        <v-col cols="12" sm="6">
          <v-btn
            prepend-icon="mdi-attachment"
            color="primary"
            variant="outlined"
            block
            class="browse-button"
            v-on:click="browseFiles"
          >
            {{ $t('menus.uploadFiles.browseFiles') }}
            <input type="file" ref="fileInput" v-on:change="filesSelected" style="display: none"
                   multiple data-cy="uploadLink-uploadFiles-inputFiles">
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn
            prepend-icon="mdi-folder-open"
            color="primary"
            variant="outlined"
            block
            class="browse-button"
            v-on:click="browseFolders"
          >
            {{ $t('menus.uploadFiles.browseFolders') }}
            <input type="file" ref="foldersInput" v-on:change="filesSelected"
                   style="display: none" multiple webkitdirectory
                   data-cy="uploadLink-uploadFiles-inputFolders">
          </v-btn>
        </v-col>
      </v-row>
      <div class="text-center mt-2 upload-hint">
        {{ $t('menus.uploadFiles.clickToAddFiles') }}
      </div>
    </div>

    <div v-if="errorMessage" class="d-flex align-center my-2 px-3 error-message" role="alert" aria-live="assertive">
      <v-icon class="mr-2" aria-hidden="true">mdi-alert-circle</v-icon>
      <span class="text-body-1">{{ errorMessage }}</span>
      <v-spacer />
      <v-btn
        @click="errorMessage = ''"
        icon="mdi-close-circle"
        size="x-small"
        variant="text"
        density="compact"
        :aria-label="$t('menus.uploadFiles.closeError')"
      />
    </div>

    <div class="position-relative">
      <!-- Upload/Cancel buttons section -->
      <div v-show="hasAddedFiles || hasUploadingFiles" class="mb-4">
        <v-row>
          <v-col cols="12" v-show="hasAddedFiles">
            <v-btn
              color="primary"
              v-on:click="uploadAllFiles"
              block
              size="x-large"
              variant="flat"
              data-cy="uploadLink-uploadFiles-uploadAllButton"
            >
              {{$t('menus.uploadFiles.uploadAll')}}
            </v-btn>
          </v-col>

          <v-col cols="12" v-show="hasUploadingFiles">
            <v-btn
              color="primary"
              v-on:click="cancelFileUploads"
              block
              size="x-large"
              variant="flat"
              data-cy="uploadLink-uploadFiles-cancelAllButton"
            >
              {{ $t('menus.uploadFiles.cancelUploads') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- File Lists Section with Virtual Scrolling -->
      <div class="file-lists-container">
        <!-- Completed Files Section -->
        <div v-if="completedFiles.length > 0" class="file-group mb-4">
          <div class="file-group-header d-flex align-center justify-space-between pa-3" @click="toggleCompletedFiles">
            <div class="d-flex align-center">
              <span>{{ $t('menus.uploadFiles.NFilesUploaded', completedFiles.length) }}</span>
              <v-chip
                size="small"
                color="success"
                class="ml-2"
              >
                {{ completedFiles.length }}
              </v-chip>
            </div>
            <v-icon>{{ showCompletedFiles ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>

          <div v-show="showCompletedFiles">
            <!-- Virtual Scroll for Completed Files -->
            <v-virtual-scroll
              :items="completedFiles"
              :item-height="96"
              :height="Math.min(completedFiles.length * 96, 500)"
            >
              <template v-slot:default="{ item }">
                <div class="px-3 py-2">
                  <div class="file-card pa-4">
                    <div class="d-flex justify-space-between align-center">
                      <div class="file-info">
                        <v-tooltip location="bottom" :z-index="1200">
                          <template v-slot:activator="{ props }">
                            <span class="file-name" v-bind="props">
                              {{ getFilenameTooltip(item).replace(/\//g, ' / ') }}
                            </span>
                          </template>
                          <span>{{ getFilenameTooltip(item) }}</span>
                        </v-tooltip>

                        <div v-bind:class="{'inline-message--error': item.status === STATUSES.ERROR}"
                             class="upload-list-item_subtitle mt-1">
                          {{ getItemSubtitle(item) }}
                        </div>
                      </div>

                      <v-btn
                        class="ml-2 remove-btn"
                        size="small"
                        icon="mdi-close"
                        variant="flat"
                        density="comfortable"
                        :aria-label="$t('menus.uploadFiles.removeFileFromList', { filename: getFilenameTooltip(item) })"
                        @click="removeSelectedFile(item)"
                        rounded="pill"
                      ></v-btn>
                    </div>
                  </div>
                </div>
              </template>
            </v-virtual-scroll>
          </div>
        </div>

        <!-- Pending Files Section -->
        <div class="file-group mb-4">
          <div class="file-group-header d-flex align-center justify-space-between pa-3" @click="togglePendingFiles">
            <div class="d-flex align-center">
              <span>{{ $t('menus.uploadFiles.pendingFiles') }}</span>
              <v-chip
                size="small"
                color="primary"
                class="ml-2"
                v-if="notCompletedFiles.length > 0"
              >
                {{ notCompletedFiles.length }}
              </v-chip>
            </div>
            <v-icon>{{ showPendingFiles ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>

          <div v-show="showPendingFiles">
            <!-- Virtual Scroll for Pending Files -->
            <v-virtual-scroll
              :items="notCompletedFiles"
              :item-height="160"
              :height="Math.min(notCompletedFiles.length * 160, 600)"
            >
              <template v-slot:default="{ item }">
                <div class="px-3 py-2">
                  <div class="file-card pa-4">
                    <div class="d-flex justify-space-between align-center">
                      <div class="file-info flex-grow-1">
                        <div class="d-flex align-center mb-1">
                          <v-tooltip location="bottom" :z-index="1200">
                            <template v-slot:activator="{ props }">
                              <span class="file-name" v-bind="props">
                                {{ getFilenameTooltip(item).replace(/\//g, ' / ') }}
                              </span>
                            </template>
                            <span>{{ getFilenameTooltip(item) }}</span>
                          </v-tooltip>
                        </div>

                        <v-list-item-subtitle v-show="item.status === STATUSES.UPLOADING" class="mt-2">
                          <v-progress-linear
                            v-bind:model-value="item.progress.percentage"
                            :aria-label="getUploadProgressLabel(item)"
                          ></v-progress-linear>
                        </v-list-item-subtitle>

                        <div class="d-flex justify-space-between mt-1">
                          <div v-if="showingShowNotesCheckbox(item)">
                            <v-checkbox
                              v-model="showNotes[item.meta.clientFileId]"
                              v-bind:label="$t('menus.uploadFiles.addNote')"
                              hide-details
                              density="compact"
                            />
                          </div>
                          <div v-bind:class="{'inline-message--error': item.status === STATUSES.ERROR}" class="upload-list-item_subtitle file-size">
                            {{ getItemSubtitle(item) }}
                          </div>
                        </div>

                        <v-textarea
                          v-if="showingShowNotesCheckbox(item)"
                          v-show="showNotes[item.meta.clientFileId]"
                          @update:model-value="setNote(item, $event)"
                          :label="$t('menus.menuUploadLink.notes')"
                          :disabled="item.status !== STATUSES.ADDED"
                          flat
                          variant="outlined"
                          density="compact"
                          rows="2"
                          hide-details
                          class="mt-2"
                        >
                        </v-textarea>
                      </div>

                      <v-btn
                        class="ml-2 remove-btn"
                        size="small"
                        icon="mdi-close"
                        variant="flat"
                        density="comfortable"
                        :aria-label="getFileActionLabel(item)"
                        @click="cancelOrCloseFileUpload(item)"
                        rounded="pill"
                      ></v-btn>
                    </div>
                  </div>
                </div>
              </template>
            </v-virtual-scroll>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-3 upload-tooltips_link">
      <a href="#" class="d-flex align-center font-italic" @click.prevent="uploadTooltipsDialog = true">
        <v-icon
          size="small"
          color="info"
          class="mr-1"
        >
          mdi-information-slab-circle
        </v-icon>
        <span>{{ $t('menus.uploadFiles.tipsForUploadingFilesTitle') }}</span>
      </a>

      <UploadingFilesTipsDialog :is-visible="uploadTooltipsDialog" @close="uploadTooltipsDialog = false" />
    </div>
  </div>
</template>

<script>
import axios, { isCancel } from 'axios';
import { useUploadStore } from '@/stores/UploadStore.js';
import { useUploadLinkStore } from '@/stores/UploadLinkStore.js';
import Helper from '@/components/helper.js';
import UploadingFilesTipsDialog from '@/components/dialogs/UploadingFilesTipsDialog.vue';
import { formatSize } from '@/components/formatters.js';

// This needs to match on the backend as well
const MULTIPART_UPLOAD_CHUNK_SIZE = 1000000000; // 1GB now;

export default {
  name: "UploadFiles",
  components: { UploadingFilesTipsDialog },
  data() {
    return {
      errorMessage: '',
      selectedFiles: [],
      showNotes: {},
      showCompletedFiles: true,
      showPendingFiles: true,
      duplicateWarning: null,

      uploaderName: '',
      uploaderNameRules: [
        ...Helper.getNameValidationRules(this, {
          required: true,
          messages: {
            required: 'menus.uploadFiles.requiredUploaderName',
          },
        }),
        // v => v.length <= 30 || 'Uploader name must be less than 30 characters.',
      ],
      uploadTooltipsDialog: false,
      clientFileIdCounter: 0, // Counter for generating unique client file IDs
    };
  },
  mounted() {
    // console.log("Upload link info", this.getUploadlinkInfo);
  },
  methods: {
    /**
     * Generates a unique client file ID for client-side identification
     * Format: file_temp_{timestamp}_{counter}_{random}
     *
     * @returns {string} Unique client file ID
     */
    generateClientFileId() {
      const timestamp = Date.now();
      const counter = this.clientFileIdCounter++;
      const random = Math.random().toString(36).substr(2, 9);
      return `file_temp_${timestamp}_${counter}_${random}`;
    },
    toggleCompletedFiles() {
      this.showCompletedFiles = !this.showCompletedFiles;
    },
    togglePendingFiles() {
      this.showPendingFiles = !this.showPendingFiles;
    },
    browseFiles() {
      this.$refs.fileInput.click();
    },
    browseFolders() {
      this.$refs.foldersInput.click();
    },
    async droppedFiles(event) {
      let filesList = [];
      let dropItems = [];

      if (event.dataTransfer && event.dataTransfer.items && event.dataTransfer.items.length > 0) {
        for (let i = 0; i < event.dataTransfer.items.length; i++) {
          dropItems.push(event.dataTransfer.items[i].webkitGetAsEntry());
        }
      }

      const uploadLinkStore = useUploadLinkStore();

      // the async read of files is on a separate step to fix an issue with chrome
      for (let i = 0; i < dropItems.length; i++) {
        if (dropItems[i]) {
          let foundFiles = await uploadLinkStore.readFilesystemItem(dropItems[i], '');
          filesList = filesList.concat(foundFiles);
        }
      }

      if (filesList.length > 0) {
        const duplicateFiles = [];

        for (const file of filesList) {
          // Only track actual duplicates (files that couldn't be added)
          if (!this.addFile(file)) {
            duplicateFiles.push(file.name);
          }
        }

        // Only show warning for duplicate files
        if (duplicateFiles.length > 0) {
          const message = duplicateFiles.length === 1
            ? this.$t('menus.uploadFiles.duplicateFile', { filename: duplicateFiles[0] })
            : this.$t('menus.uploadFiles.duplicateFiles', { count: duplicateFiles.length });

          this.duplicateWarning = message;
        }

        this.$refs.fileInput.value = null;
        this.$refs.foldersInput.value = null;
      }
    },
    filesSelected(event) {
      if (event.target.files && event.target.files.length > 0) {
        const duplicateFiles = [];

        for (const file of event.target.files) {
          // Only track actual duplicates (files that couldn't be added)
          if (!this.addFile(file)) {
            duplicateFiles.push(file.name);
          }
        }

        // Only show warning for duplicate files
        if (duplicateFiles.length > 0) {
          const message = duplicateFiles.length === 1
            ? this.$t('menus.uploadFiles.duplicateFile', { filename: duplicateFiles[0] })
            : this.$t('menus.uploadFiles.duplicateFiles', { count: duplicateFiles.length });

          this.duplicateWarning = message;
        }

        this.$refs.fileInput.value = null;
        this.$refs.foldersInput.value = null;
      }
    },
    setNote(item, note) {
      const itemIndex = this.selectedFiles.indexOf(item);
      if (itemIndex > -1) {
        this.selectedFiles[itemIndex].note = note;
      }
    },
    /**
     * Adds a file to the upload queue after checking for duplicates.
     *
     * Duplicate detection logic:
     * - Files are considered duplicates only if they have the SAME full path AND size
     * - Full path includes folder structure (webkitRelativePath)
     * - This allows files with the same name in different folders
     *
     * Examples:
     * ✓ FolderA/document.txt + FolderB/document.txt = NOT duplicates (different paths)
     * ✓ FolderA/document.txt + FolderA/document.txt = duplicates (same path)
     * ✓ document.txt (root) + FolderA/document.txt = NOT duplicates (different paths)
     * ✓ document.txt (root) + document.txt (root) = duplicates (same path)
     *
     * @param {File} file - File object to add
     * @returns {boolean} - True if file was added, false if duplicate detected
     */
    addFile(file) {
      // Check for duplicate files first
      const fullPath = file.webkitRelativePath || file.name;
      const normalizedPath = fullPath.replace(/\\/g, '/');

      const existingFile = this.selectedFiles.find((existingFile) => {
        const existingPath = existingFile.meta.webkitRelativePath || existingFile.meta.filename;
        const existingNormalized = existingPath.replace(/\\/g, '/');

        return existingNormalized === normalizedPath &&
          existingFile.meta.filesize === file.size;
      });

      if (existingFile) {
        return false;
      }

      // Get max file size limit
      const uploadLinkInfo = this.getUploadlinkInfo;
      const maxFilesize = uploadLinkInfo?.params?.maxfilesize || 500000000000;

      // Prepare file data
      const clientFileId = this.generateClientFileId();
      const data = {
        file: file,
        status: this.STATUSES.ADDED,
        note: '',
        errorMessage: '',
        progress: {
          percentage: 0,
          uploaded: 0,
        },
        cancelUpload: () => {},
        meta: {
          clientFileId: clientFileId, // Temporary client-side identifier
          fileuuid: null, // Will be populated with backend UUID after creation
          filename: file.name,
          filesize: file.size,
          useruuid: this.getUploadlinkInfo.createdby.uuid,
          nodeid: this.getUploadlinkInfo.nodeid,
          webkitRelativePath: normalizedPath,
          lastModified: file?.lastModified || '',
        },
      };

      // Check if file exceeds max size and set error status immediately
      if (file.size > maxFilesize) {
        data.status = this.STATUSES.ERROR;
        const maxSizeMB = Math.floor(maxFilesize / 1_000_000);
        data.errorMessage = `Upload or processing failed.You cannot upload file larger than ${maxSizeMB} MB.`;
      }

      this.selectedFiles.push(data);
      return true;
    },
    removeSelectedFile(item) {
      this.selectedFiles = this.selectedFiles.filter((x) => x.meta.clientFileId !== item.meta.clientFileId);
    },
    cancelOrCloseFileUpload(item) {
      const record = this.selectedFiles.find((x) => x.meta.clientFileId === item.meta.clientFileId);

      if (record.status === this.STATUSES.CANCELLED) {
        this.removeSelectedFile(record);
        return
      }

      if (record) {
        record.cancelUpload();
        record.status = this.STATUSES.CANCELLED;
      }
    },
    async isTargetNodeAvailable() {
      const uploadLinkStore = useUploadLinkStore();

      try {
        await uploadLinkStore.checkTargetNodeAvailability();
        return true;
      } catch (error) {
        this.setUploadError(error);
        return false;
      }
    },
    setUploadError(error) {
      const responseData = error?.response?.data;

      if (responseData?.errors?.[0]?.issue) {
        this.errorMessage = responseData.errors[0].issue;
      } else if (responseData?.error?.message) {
        this.errorMessage = responseData.error.message;
      } else {
        console.error("Error uploading the files", error);
        this.errorMessage = "An error occurred during upload";
      }
    },
    async uploadAllFiles() {
      this.errorMessage = '';

      // Validate form.
      const validForm = await this.$refs.userDataForm.validate();
      if (!validForm.valid) {
        return;
      }

      if (!await this.isTargetNodeAvailable()) {
        return;
      }

      // Track files being added in THIS upload operation to prevent interference from concurrent uploads
      const newWaitingFiles = [];

      // Update status for files ADDED in this batch
      for (const file of this.selectedFiles) {
        if (file.status === this.STATUSES.ADDED) {
          if (file.meta.filesize === 0) {
            file.status = this.STATUSES.ERROR;
            file.errorMessage = 'The file is empty';
          } else {
            file.status = this.STATUSES.WAITING;
            newWaitingFiles.push(file); // Track this file as part of THIS batch
          }
        }
      }

      // CRITICAL: Only upload files from THIS batch (excludes files from concurrent uploads)
      // This prevents files from Request 1 that are still WAITING from being included in Request 2
      const filesToUpload = this.selectedFiles.filter((file) => {
        return file.status === this.STATUSES.WAITING &&
          newWaitingFiles.includes(file); // Only files from THIS batch
      });

      if (filesToUpload.length < 1) {
        return;
      }

      const totalSize = filesToUpload.reduce((sum, file) => sum + file.meta.filesize, 0);

      try {
        // Check storage availability
        const uploadLinkStore = useUploadLinkStore();
        const checkResponse = await uploadLinkStore.checkStorage(totalSize);
        if (checkResponse && checkResponse.data && checkResponse.data.willExceedLimit) {
          throw {
            response: {
              data: {
                error: {
                  message: this.$t('menus.uploadFiles.insufficientStorage')
                }
              }
            }
          };
        }

        // Store the file map by client file ID BEFORE sending to backend
        const clientFileIdMap = new Map();
        for (const file of filesToUpload) {
          clientFileIdMap.set(file.meta.clientFileId, file);
          console.log(`Storing file: clientFileId=${file.meta.clientFileId}, filename=${file.meta.filename}`);
        }

        // Prepare hierarchical payload with client file IDs
        const hierarchicalPayload = uploadLinkStore.prepareHierarchicalPayload(
          filesToUpload,
          this.uploaderName,
          this.getUploadlinkInfo.createdby.uuid
        );
        console.log('hierarchicalPayload', hierarchicalPayload);

        // Send hierarchical structure to create pods
        const response = await uploadLinkStore.createPods(hierarchicalPayload);
        console.log('Response from backend:', response);

        // Response is now a flat array of pods
        const podsData = response;

        if (!Array.isArray(podsData)) {
          console.error('Backend response is not an array');
          throw new Error('Invalid backend response: expected array of pods');
        }

        console.log('Pods received from backend:', podsData.length);

        // Process the response and prepare files for upload
        const filesReadyForUpload = [];

        for (const podData of podsData) {
          const clientFileId = podData.clientFileId;
          if (!clientFileId) {
            console.error('Pod response missing clientFileId:', podData);
            continue;
          }

          const file = clientFileIdMap.get(clientFileId);

          if (!file) {
            console.error(`File with clientFileId ${clientFileId} not found in clientFileIdMap`);
            console.error('Available client file IDs in map:', Array.from(clientFileIdMap.keys()));
            continue;
          }

          // Map backend UUID from pod data
          if (podData.fileuuid) {
            file.meta.fileuuid = podData.fileuuid;
            console.log(`Mapped clientFileId=${clientFileId} to backendUuid=${podData.fileuuid} for file=${file.meta.filename}`);
          } else {
            console.error(`Pod response missing fileuuid for clientFileId=${clientFileId}`);
            continue;
          }

          console.log(`Processing pod for file: clientFileId=${clientFileId}, backendUuid=${file.meta.fileuuid}, filename=${file.meta.filename}`);

          if (podData.errormessage) {
            file.status = this.STATUSES.ERROR;
            file.errorMessage = podData.errormessage;
            continue;
          }

          // Store all backend data needed for upload
          file.uploadData = {
            backendFileuuid: file.meta.fileuuid, // Use the mapped backend UUID for S3 upload
            podid: podData.podid,
            presignedPost: podData.presignedPost,
            multipartPost: podData.multipartPost,
            clientFileId: clientFileId, // Keep client file ID for reference
            actualFolderPath: podData.actualFolderPath,
          };

          // Log the mapping for debugging
          console.log(`File ready for upload: clientFileId=${clientFileId}, backendUuid=${file.meta.fileuuid}, S3Key=public/${file.meta.fileuuid}`);

          filesReadyForUpload.push(file);
        }

        console.log(`Total files ready for upload: ${filesReadyForUpload.length}`);

        // Only upload files that got valid responses
        for (const file of filesReadyForUpload) {
          if (file.status !== this.STATUSES.WAITING || !file.uploadData) {
            continue;
          }

          console.log(`Uploading file: ${file.meta.filename} to S3 key: public/${file.uploadData.backendFileuuid}`);

          file.status = this.STATUSES.UPLOADING;

          if (file.uploadData.presignedPost) {
            await this.uploadPresignedFile(file);
          } else if (file.uploadData.multipartPost) {
            await this.uploadMultipartFile(file);
          }

          // Update metadata after successful upload
          if (file.status === this.STATUSES.FINISHED) {
            console.log(`Upload completed: ${file.meta.filename}, backend UUID: ${file.meta.fileuuid}`);
            file.meta.podid = file.uploadData.podid;
          }

          // Clear upload data after processing
          delete file.uploadData;
        }
      } catch (error) {
        this.setUploadError(error);

        for (const file of this.selectedFiles) {
          if (file.status === this.STATUSES.ADDED || file.status === this.STATUSES.WAITING) {
            file.status = this.STATUSES.CANCELLED;
          }
        }
      }
    },
    async uploadPresignedFile(file) {
      const formData = new FormData();
      const url = file.uploadData.presignedPost.url;
      const fields = file.uploadData.presignedPost.fields;
      const abortUploadController = new AbortController();

      file.cancelUpload = abortUploadController.abort.bind(abortUploadController);

      const options = {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progress) => {
          file.progress.percentage = parseInt((progress.loaded / progress.total) * 100);
          file.progress.uploaded = progress.loaded;
        },
        signal: abortUploadController.signal,
      };

      for (const key in fields) {
        formData.append(key, fields[key]);
      }
      formData.append('file', file.file);

      try {
        await axios.post(url, formData, options);
        file.status = this.STATUSES.FINISHED;
      } catch (error) {
        if (isCancel(error)) {
          file.status = this.STATUSES.CANCELLED;
          return;
        } else {
          if (error &&
            error.response &&
            error.response.data &&
            error.response.data.error &&
            error.response.data.error.message) {
            file.errorMessage = error.response.data.error.message;
          } else {
            console.error("Error uploading the file", error);
          }
          file.status = this.STATUSES.ERROR;
        }
      }
    },
    async uploadMultipartFile(file) {
      const fileSize = file.file.size;
      const totalChunks = Math.ceil(fileSize / MULTIPART_UPLOAD_CHUNK_SIZE);
      const uploadId = file.uploadData.multipartPost.UploadId;
      const presignedUrls = file.uploadData.multipartPost.PresignedUrls;

      // Create abort controller for cancellation
      const abortUploadController = new AbortController();
      file.cancelUpload = abortUploadController.abort.bind(abortUploadController);

      try {
        const uploadedParts = [];
        let uploadedBytes = 0;

        // Upload each chunk
        for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
          // Check if upload was cancelled
          if (abortUploadController.signal.aborted) {
            throw new Error('Upload cancelled');
          }

          const start = (partNumber - 1) * MULTIPART_UPLOAD_CHUNK_SIZE;
          const end = Math.min(start + MULTIPART_UPLOAD_CHUNK_SIZE, fileSize);
          const chunk = file.file.slice(start, end);

          // Get the presigned URL for this part (array is 0-indexed, partNumber is 1-indexed)
          const presignedUrl = presignedUrls[partNumber - 1];

          if (!presignedUrl) {
            throw new Error(`No presigned URL for part ${partNumber}`);
          }

          const options = {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/octet-stream',
            },
            signal: abortUploadController.signal,
            onUploadProgress: (progress) => {
              const processedBytes = (partNumber - 1) * MULTIPART_UPLOAD_CHUNK_SIZE + progress.loaded;
              file.progress.percentage = parseInt((processedBytes / fileSize) * 100);
              file.progress.uploaded = processedBytes;
            },
          };

          const response = await axios.put(presignedUrl, chunk, options);
          const etag = response.headers.etag || response.headers.ETag;
          uploadedParts.push({
            PartNumber: partNumber,
            ETag: etag.replace(/"/g, '')
          });

          uploadedBytes += chunk.size;
          file.progress.percentage = Math.round((uploadedBytes / fileSize) * 100);
          file.progress.uploaded = uploadedBytes;
        }

        const completeMultipartPayload = {
          podid: file.uploadData.podid,
          uploadid: uploadId,
          uploadedparts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        };

        // Complete the multipart upload
        await this.completeMultipartUpload(completeMultipartPayload);

        file.status = this.STATUSES.FINISHED;

      } catch (error) {
        if (isCancel(error) || abortUploadController.signal.aborted) {
          file.status = this.STATUSES.CANCELLED;
          try {
            await this.abortMultipartUpload(uploadId, file);
          } catch (abortError) {
            console.error("Failed to abort multipart upload", abortError);
          }
          return;
        }

        // Handle error
        console.error("Error uploading multipart file", error);

        if (error && error.response && error.response.data &&
          error.response.data.error && error.response.data.error.message) {
          file.errorMessage = error.response.data.error.message;
        } else {
          file.errorMessage = error.message || 'Unknown error occurred during upload';
        }

        file.status = this.STATUSES.ERROR;
      }
    },
    async completeMultipartUpload(completeMultipartPayload) {
      const uploadLinkStore = useUploadLinkStore();

      try {
        const response = await uploadLinkStore.completeMultipartUpload(completeMultipartPayload);
        return response;
      } catch (error) {
        // Preserve the original error as the `cause` so callers (and linters) can access it
        throw new Error(
          `Failed to complete multipart upload: ${error?.message ?? String(error)}`,
          { cause: error }
        );
      }
    },
    async abortMultipartUpload(uploadId, file) {
      const uploadLinkStore = useUploadLinkStore();

      const abortPayload = {
        uploadid: uploadId,
        fileuuid: file.meta.fileuuid, // Use backend UUID (already mapped)
      };

      try {
        const response = await uploadLinkStore.abortMultipartUpload(abortPayload);
        return response;
      } catch (error) {
        console.error("Failed to abort multipart upload", error);
        throw error;
      }
    },
    async cancelFileUploads() {
      const cancelFileUploads = this.selectedFiles.filter((file) => {
        return file.status === this.STATUSES.WAITING || file.status === this.STATUSES.UPLOADING;
      });

      if (cancelFileUploads.length === 0) {
        return;
      }

      for (const file of cancelFileUploads) {
        if (file.status === this.STATUSES.UPLOADING) {
          file.cancelUpload(); // Abort the current upload
          // Status will be set to CANCELLED in the upload method
        } else if (file.status === this.STATUSES.WAITING) {
          file.status = this.STATUSES.CANCELLED; // Mark waiting files as cancelled
        }
      }

      // Cancel files on the backend - use backend UUIDs (only if they were already mapped)
      const fileuuids = cancelFileUploads
        .filter((x) => x.meta.fileuuid) // Only include files that have backend UUIDs
        .map((x) => x.meta.fileuuid);

      const uploadLinkStore = useUploadLinkStore();
      await uploadLinkStore.cancelUploads({ fileuuids: fileuuids });
    },
    startUploading() {
      const hasFilesForUploading = this.selectedFiles.some((file) => file.status === this.STATUSES.ADDED);
      const cleanUploaderName = this.uploaderName.trim();

      if (hasFilesForUploading && cleanUploaderName.length > 0) {
        this.uploadAllFiles();
      }
    },
    showingShowNotesCheckbox(item) {
      return item.status == this.STATUSES.ADDED;
    },
    getItemSubtitle(item) {
      let text;
      switch (item.status) {
        case this.STATUSES.ADDED:
          text = "(" + formatSize(item.meta.filesize, 0) + ")";
          break;
        case this.STATUSES.WAITING:
          text = this.$t('menus.uploadFiles.statusWaiting');
          break;
        case this.STATUSES.UPLOADING:
          text = this.$t(
            'menus.uploadFiles.statusUploading',
            [formatSize(item.progress.uploaded, 1), formatSize(item.meta.filesize, 1)]
          );
          break;
        case this.STATUSES.FINISHED:
          text = this.$t('menus.uploadFiles.statusFinished');
          break;
        case this.STATUSES.ERROR:
          text = this.$t('menus.uploadFiles.statusError');
          if (item.errorMessage) {
            if (typeof item.errorMessage === 'object') {
              text += this.$replaceT(item.errorMessage.code, item.errorMessage.params, item.errorMessage.content);
            } else {
              text += item.errorMessage;
            }
          }
          break;
        case this.STATUSES.CANCELLED:
          text = this.$t('menus.uploadFiles.statusCancelled');
          break;
        default:
          text = this.$t('menus.uploadFiles.statusUnknown');
          break;
      }
      return text;
    },
    getFilenameTooltip(item) {
      if (item && item.meta && item.meta.webkitRelativePath) {
        return item.meta.webkitRelativePath;
      }
      return item.meta.filename;
    },
    getFileActionLabel(item) {
      const filename = this.getFilenameTooltip(item);

      if (item.status === this.STATUSES.WAITING || item.status === this.STATUSES.UPLOADING) {
        return this.$t('menus.uploadFiles.cancelUploadForFile', { filename });
      }

      return this.$t('menus.uploadFiles.removeFile_', { filename });
    },
    getUploadProgressLabel(item) {
      return this.$t('menus.uploadFiles.uploadingFileProgress', {
        filename: this.getFilenameTooltip(item),
        percentage: item.progress.percentage,
      });
    },
  },
  computed: {
    maxfilesize() {
      const uploadLinkStore = useUploadLinkStore();
      const uploadLink = uploadLinkStore.getUploadlinkInfo;

      let maxFilesize = uploadLink?.params.maxfilesize || 500000000000;
      maxFilesize = formatSize(maxFilesize, 0);

      return maxFilesize;
    },
    getUploadlinkInfo() {
      const uploadLinkStore = useUploadLinkStore();
      return uploadLinkStore.getUploadlinkInfo;
    },
    getBrandingInfo() {
      if (this.getUploadlinkInfo && this.getUploadlinkInfo.brand) {
        return this.getUploadlinkInfo.brand;
      }

      return null;
    },
    STATUSES() {
      const uploadStore = useUploadStore();
      return uploadStore.statuses;
    },
    completedFiles() {
      return this.selectedFiles
        .filter((item) => item.status === this.STATUSES.FINISHED)
        .sort((a, b) => {
          const pathA = a.meta?.webkitRelativePath || '';
          const pathB = b.meta?.webkitRelativePath || '';
          return pathA.localeCompare(pathB);
        });
    },
    notCompletedFiles() {
      return this.selectedFiles
        .filter((item) => item.status !== this.STATUSES.FINISHED)
        .sort((a, b) => {
          const pathA = a.meta?.webkitRelativePath || '';
          const pathB = b.meta?.webkitRelativePath || '';
          return pathA.localeCompare(pathB);
        });
    },
    hasAddedFiles() {
      for (let i = 0; i < this.selectedFiles.length; i++) {
        if (this.selectedFiles[i].status === this.STATUSES.ADDED) {
          return true;
        }
      }
      return false;
    },
    hasUploadingFiles() {
      return this.selectedFiles
        .some((file) => file.status === this.STATUSES.UPLOADING || file.status === this.STATUSES.WAITING);
    },
  }
};
</script>

<style scoped>
.position-relative {
  position: relative;
}

.file-lists-container {
  background: transparent;
}

.file-group {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.v-theme--dark .file-group {
  border-color: rgba(255, 255, 255, 0.1);
}

.file-group-header {
  cursor: pointer;
  user-select: none;
  background-color: rgb(var(--v-theme-tw-surface-accent));
}

.v-theme--light .file-group-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.v-theme--dark .file-group-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.file-card {
  margin: 0;
  background-color: transparent;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: all 0.2s ease;
  background-color: rgb(var(--v-theme-tw-surface-accent));
}

.v-theme--light .file-card {
  border-color: #e0e0e0;
}

.v-theme--dark .file-card {
  border-color: #333;
}

.upload-list-item_subtitle {
  font-size: 0.85rem;
  opacity: 0.8;
}

.file-size {
  text-align: right;
  font-weight: 500;
}

.file-name {
  font-weight: 500;
  word-break: break-all;
}

.file-info {
  overflow: hidden;
  flex-grow: 1;
}

/* Ensure round buttons */
.v-btn.rounded-pill {
  border-radius: 50%;
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
}

/* More visible remove buttons */
.remove-btn {
  transition: all 0.2s ease;
}

.remove-btn:hover {
  transform: scale(1.1);
}

.v-theme--light .remove-btn {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.v-theme--dark .remove-btn {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

/* File browser section */
.file-browser-section {
  padding: 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background-color: rgb(var(--v-theme-tw-surface-accent));
}

.v-theme--light .file-browser-section {
  border: 1px dashed rgba(0, 0, 0, 0.2);
}

.v-theme--dark .file-browser-section {
  border: 1px dashed rgba(255, 255, 255, 0.2);
}

.file-browser-section:hover {
  border-color: var(--v-theme-primary);
}

.browse-button {
  height: 48px;
  font-weight: 500;
}

.upload-hint {
  font-size: 0.85rem;
  opacity: 0.7;
}

.warning-message {
  background-color: #d97e3d;
  border: 1px solid rgba(var(--v-theme-warning), 0.3);
  border-radius: 4px;
  color: white;
  font-weight: 500;
}

.error-message {
  background-color: rgb(var(--v-theme-error));
  border: 1px solid rgba(var(--v-theme-error), 0.5);
  border-radius: 4px;
  color: white;
  font-weight: 500;
}
</style>
